import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {useHistory} from 'react-router-dom';
import {Breadcrumbs, FlexColumn, Link} from '@forecast-it/design-system';
import {CommitMutation} from '../../../../../mutations/CommitMutation';
import {TopHeaderBar} from '../../../../shared/components/headers/top-header-bar/TopHeaderBar';
import {useTrackPage} from '../../../../../tracking/amplitude/hooks/useTrackPage';
import type {CreateBaselineTemplatePageQuery$data} from './__generated__/CreateBaselineTemplatePageQuery.graphql';
import {BASELINE_TEMPLATES_PATH} from '../../templatesPaths';
import {isPlainClick} from '../../../../shared/util/LinkClickUtil';
import {BaselineTemplateForm, CommitTemplate} from '../form/BaselineTemplateForm';
import {blankTemplate, splitOptions} from '../form/baselineTemplateDraft';
import CreateBaselineTemplateMutation from './CreateBaselineTemplateMutation';

export const CreateBaselineTemplatePageQuery = graphql`
	query CreateBaselineTemplatePageQuery {
		viewer {
			actualPersonId
			component(name: "settings_create_baseline_template")
			company {
				id
				currency
				roles(first: 10000, includeDisabled: false) {
					edges {
						node {
							id
							name
							disabled
						}
					}
				}
				expenseCategories(first: 10000) {
					edges {
						node {
							id
							name
							disabled
						}
					}
				}
				baselineTemplates {
					id
					name
				}
			}
		}
	}
`;

type Props = {viewer: CreateBaselineTemplatePageQuery$data['viewer']};

// No automatic retry: create is not idempotent, so a resend after a lost response would hit the unique
// name index and report a name the first request had already saved.
const commitCreate: CommitTemplate = (request, onSuccess, onError) =>
	CommitMutation(CreateBaselineTemplateMutation, request, () => onSuccess(), false, null, onError);

export const CreateBaselineTemplatePage = ({viewer}: Props): JSX.Element | null => {
	useTrackPage('Create Baseline Template');
	const {formatMessage} = useIntl();
	const history = useHistory();
	const company = viewer?.company;

	// includeDisabled: false is not honoured by the company service, so disabled roles are dropped here too,
	// the same as categories. The server refuses a template that uses one.
	const roles = useMemo(
		() =>
			splitOptions(
				(company?.roles?.edges ?? []).map(edge => edge?.node),
				name => name
			).enabled,
		[company]
	);
	const categories = useMemo(
		() =>
			splitOptions(
				(company?.expenseCategories?.edges ?? []).map(edge => edge?.node),
				name => name
			).enabled,
		[company]
	);
	const existingNames = useMemo(
		() => (company?.baselineTemplates ?? []).flatMap(template => (template?.name ? [template.name] : [])),
		[company]
	);

	const initialDraft = useMemo(blankTemplate, []);

	if (!company) {
		return null;
	}
	return (
		<FlexColumn gap="l">
			<Breadcrumbs>
				<Breadcrumbs.Item isDisabled>
					<span>{formatMessage({id: 'baseline_templates.breadcrumb.admin'})}</span>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					{/* The design-system Link carries the link colour. The click goes through the router, so
					    leaving with unsaved changes still asks first, the same as Cancel. */}
					<Link
						href={BASELINE_TEMPLATES_PATH}
						onClick={(event: React.MouseEvent) => {
							if (!isPlainClick(event)) {
								return;
							}
							event.preventDefault();
							history.push(BASELINE_TEMPLATES_PATH);
						}}
						data-cy="baseline-template-breadcrumb-templates"
					>
						{formatMessage({id: 'settings.templates.title'})}
					</Link>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					<span>{formatMessage({id: 'baseline_templates.breadcrumb.new'})}</span>
				</Breadcrumbs.Item>
			</Breadcrumbs>
			<TopHeaderBar title={formatMessage({id: 'baseline_templates.create_template'})} />
			<BaselineTemplateForm
				roles={roles}
				categories={categories}
				currency={company.currency ?? 'USD'}
				existingNames={existingNames}
				initialDraft={initialDraft}
				editing={false}
				commit={commitCreate}
			/>
		</FlexColumn>
	);
};
